import { LOCAL_STORAGE_KEYS } from 'constant/localStorage';
import { emitErrorLog, emitFatalLog, emitWarnLog } from './logger';
import { serializeError } from './serializeError';
import { SeverityNumber } from '@opentelemetry/api-logs';
// ---------------------------------------------------------------------------
// Long-task / frozen-page monitor
//
// Problem: "This page isn't responding" (Chrome) appears after ~5 s of main-
// thread blockage. JavaScript cannot intercept that dialog — the thread is
// frozen when it appears, and killed when the user clicks "Exit page".
//
// Solution: observe long tasks BEFORE the freeze becomes visible. Any task
// > 3 000 ms is effectively user-visible freezing and a direct precursor to
// the unresponsive dialog. Log it to SigNoz so the team can correlate with
// specific pages and identify the code path causing the block.
//
// API preference order:
//   1. PerformanceLongAnimationFrame (Chrome 123+) — gives more detail
//      (script source, forced-style/layout duration, blocking duration)
//   2. PerformanceObserver 'longtask' — broad support, less detail

// ---------------------------------------------------------------------------
export function startLongTaskMonitor() {
  // Anything below this threshold is a "jank" warning; above FREEZE_THRESHOLD
  // it becomes a critical error (the OS-level unresponsive dialog territory).
  const WARN_THRESHOLD_MS = 1000; // 1000ms is the threshold for a warning
  const FREEZE_THRESHOLD_MS = 4000; // 4000ms is the threshold for a freeze

  const handleEntry = (
    entry: PerformanceLongAnimationFrameTiming | PerformanceEntry,
  ) => {
    const duration = entry.duration;
    if (duration < WARN_THRESHOLD_MS) return;

    const userId =
      localStorage.getItem(LOCAL_STORAGE_KEYS.USER_ID) ?? 'UnknownUser';
    const screenName = window.location.pathname;

    // Build a structured payload — include LoAF-specific fields when available.
    const isFrozen = duration >= FREEZE_THRESHOLD_MS;

    // LoAF entries carry extra fields that identify the blocking script.
    // TypeScript's DOM lib may not include LoAF types in older compiler versions
    // so we augment the cast with the fields we need.
    const loafEntry = entry as {
      scripts?: Array<{
        // URL of the JS file containing the blocking code
        sourceURL?: string;
        // Function name at the blocking call site (populated when source maps
        // are available in the browser)
        sourceFunctionName?: string;
        // How the script was invoked: 'user-callback', 'event-listener',
        // 'resolve-promise', 'reject-promise', 'classic-script', 'module-script'
        invokerType?: string;
        // Specific invoker string, e.g. "Element.onclick", "setTimeout handler",
        // "Promise.then" — the most actionable field for root-cause diagnosis
        invoker?: string;
        duration?: number;
      }>;
      blockingDuration?: number;
      styleAndLayoutDuration?: number;
    };

    const styleLayoutMs = Math.round(loafEntry.styleAndLayoutDuration ?? 0);
    const totalMs = Math.round(duration);
    const scriptMs = totalMs - styleLayoutMs;

    // Derive a human-readable primary cause so SigNoz logs can be filtered
    // by cause without parsing nested script arrays.
    //
    // forced-layout-reflow: reading layout properties (offsetHeight,
    //   getBoundingClientRect) after a DOM write in the same frame.
    //   Fix: batch reads/writes or use ResizeObserver.
    //
    // script-execution: a synchronous JS function ran too long.
    //   Fix: defer with setTimeout/requestIdleCallback or move to a Worker.
    let primaryCause: string;
    if (styleLayoutMs > scriptMs && styleLayoutMs > 100) {
      primaryCause = 'forced-layout-reflow';
    } else if (loafEntry.scripts && loafEntry.scripts.length > 0) {
      const topScript = [...loafEntry.scripts].sort(
        (a, b) => (b.duration ?? 0) - (a.duration ?? 0),
      )[0];
      // invoker is the most human-readable: "Element.onclick", "Promise.then", etc.
      primaryCause =
        topScript.invoker ?? topScript.invokerType ?? 'script-execution';
    } else {
      primaryCause = 'unknown';
    }

    // Only include scripts that individually contributed meaningfully (> 50 ms).
    // Sort descending so the top offender is always first in the log.
    const scripts = loafEntry.scripts
      ?.map((s) => ({
        source: s.sourceURL ?? 'unknown',
        // function_name names the exact function executing when the block occurred
        // (only populated when browser has access to source maps).
        function_name: s.sourceFunctionName || undefined,
        invoker: s.invoker ?? s.invokerType ?? 'unknown',
        duration_ms: s.duration ?? 0,
      }))
      ?.filter((s) => s.duration_ms > 50)
      ?.sort((a, b) => b.duration_ms - a.duration_ms);

    const body = {
      message: isFrozen
        ? 'Main thread frozen — likely caused "This page isn\'t responding"'
        : 'Main thread jank — long task detected',
      primary_cause: primaryCause,
      duration_ms: totalMs,
      blocking_duration_ms: Math.round(loafEntry.blockingDuration ?? 0),
      style_layout_duration_ms: styleLayoutMs,
      screen: screenName,
      ...(scripts?.length ? { scripts } : {}),
    };

    const attrs = {
      source: 'LongTaskMonitor',
      'user.id': userId,
      'url.path_template': screenName,
    };

    if (isFrozen) {
      emitFatalLog({ body, attrs, severityNumber: SeverityNumber.FATAL3 });
    } else {
      emitWarnLog({ body, attrs, severityNumber: SeverityNumber.WARN2 });
    }
  };

  // Prefer Long Animation Frame API (richer data, Chrome 123+).
  if ('PerformanceLongAnimationFrameTiming' in window) {
    const loafObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        handleEntry(entry);
      }
    });
    loafObserver.observe({ type: 'long-animation-frame', buffered: true });
    return;
  }

  // Fallback: Long Tasks API (Chrome 58+, supported broadly).
  if (PerformanceObserver.supportedEntryTypes?.includes('longtask')) {
    const ltObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        handleEntry(entry);
      }
    });
    ltObserver.observe({ type: 'longtask', buffered: true });
  }
}

// ---------------------------------------------------------------------------
// Global uncaught error monitor
//
// Covers the two error classes that React's ErrorBoundary and useErrorHandler
// cannot intercept:
//
//   window 'error'              — synchronous throws outside React's render tree:
//                                 setTimeout callbacks, raw DOM event listeners,
//                                 third-party library code, failed dynamic imports.
//
//   window 'unhandledrejection' — Promise rejections with no .catch() handler:
//                                 fire-and-forget fetches, unawaited async calls,
//                                 missed catch branches in async/await code.
//
// Rate limiting: a token-bucket (10 errors / 30 s) prevents flooding SigNoz
// if a bug causes a cascading error storm (e.g., a bad useEffect that throws
// on every render cycle).
//
// Cross-origin noise filter: browser extensions and third-party iframes produce
// "Script error." with no stack — these are dropped because they are not
// actionable and would pollute the error log.
// ---------------------------------------------------------------------------
export function startGlobalErrorMonitor() {
  // Token-bucket rate limiter shared by both listeners.
  const MAX_PER_WINDOW = 10;
  const WINDOW_MS = 30_000;
  let count = 0;
  let windowStart = Date.now();

  const shouldLog = (): boolean => {
    const now = Date.now();
    if (now - windowStart > WINDOW_MS) {
      count = 0;
      windowStart = now;
    }
    if (count >= MAX_PER_WINDOW) return false;
    count++;
    return true;
  };

  const commonAttrs = () => ({
    source: 'GlobalErrorHandler',
    'user.id':
      localStorage.getItem(LOCAL_STORAGE_KEYS.USER_ID) ?? 'UnknownUser',
    'url.path_template': window.location.pathname,
  });

  // --- Uncaught synchronous errors ---
  // event.error is the actual thrown value; event.message/filename/lineno/colno
  // are populated by the browser independently of the Error object.
  window.addEventListener('error', (event) => {
    const msg = event.message ?? '';

    // "Script error." is all the browser can report for cross-origin scripts
    // (extensions, third-party iframes) when CORS blocks full error details.
    // Drop them — there is nothing actionable here.
    if (msg === 'Script error.' || msg === 'Script error') return;
    if (!shouldLog()) return;

    emitErrorLog({
      body: {
        message: 'Uncaught error',
        error_message: msg,
        filename: event.filename || undefined,
        lineno: event.lineno || undefined,
        colno: event.colno || undefined,
        // Serialize the thrown value the same way useErrorHandler does so log
        // shape is consistent across all error sources in SigNoz.
        ...serializeError(event.error),
      },
      attrs: commonAttrs(),
    });
  });

  // --- Unhandled Promise rejections ---
  // event.reason is whatever was passed to Promise.reject() or thrown inside
  // an async function — could be an Error, AxiosError, string, or anything.
  window.addEventListener('unhandledrejection', (event) => {
    if (!shouldLog()) return;

    // ZoneContextManager (used by OTel's ZoneContextManager) patches XHR and
    // Promises with Zone.js, which re-emits rejected promises as
    // 'unhandledrejection' even when React Query's onError callback has already
    // handled them. Filter out AxiosErrors with 4xx status — these are expected
    // application errors (validation, conflict, auth) logged by useErrorHandler.
    // Only capture network failures (no response) and truly unexpected throws.
    const reason = event.reason as {
      name?: string;
      response?: { status?: number };
    };
    if (
      reason?.name === 'AxiosError' &&
      reason?.response?.status != null &&
      reason.response.status < 500
    ) {
      return;
    }

    emitErrorLog({
      body: {
        message: 'Unhandled promise rejection',
        // serializeError handles Error, AxiosError, string, plain object, and
        // primitives — the same cases as in useErrorHandler.
        ...serializeError(event.reason),
      },
      attrs: commonAttrs(),
    });
  });
}
